import { fen2yuan, formatTime } from '../utils/index';
import { GovCVData, IPromCVData, IRewardItem, IRewardList, RefundCvType, VoucherItem, RegistState, GovCVDataRes2022, CouponType } from '../type';
import { getRequest } from './base';

export const GOV_LIST_2022 = ['CV1881202204028786', 'CV1881202204026105', 'CV1881202204020601', 'CV1881202204025847'];

// 缓存数据，防止多次调用
let govCVDataRes: Promise<GovCVDataRes2022> | null = null;

/**
 * 格式化消费券数据
 * @param cv
 */
function fixVoucherItem(cv: any): VoucherItem {
  return {
    /** 券批次状态 */
    cvStatus: cv.cv_status,
    /** 消费券状态 */
    transState: cv.trans_state,
    /** 消费券转移状态 */
    ownState: cv.own_state,
    /** 余额 */
    balance: fen2yuan(cv.balance),
    /** 原始余额 */
    srcBalance: cv.balance,
    /** 创建时间 */
    createTime: cv.create_time,
    /** 有效期 */
    validTime: cv.valid_time,
    /** 过期时间 */
    expireTime: formatTime(cv.expire_time),
    srcExpireTime: cv.expire_time,
    /** 用户券ID */
    userCvId: cv.user_cv_id,
    /** 券批次号 */
    cvId: cv.cv_id,
    /** 券logo */
    cvIcon: cv.cv_icon,
    /** 券名字 */
    cvName: cv.cv_name,
    /** 券描述 */
    cvDesc: cv.cv_desc,
    /** 券类型 */
    cvType: cv.cv_type,
    /** 是否推荐该券 */
    recommend: cv.recommend,
    /** 若是主动领取消费券，则该字段是点击领取的时间，被动领取与FcreateTime相同。 */
    receiveTime: cv.receive_time,
    /** 修改时间 */
    modifyTime: cv.modify_time,
    /** 累积实际撤回金额 */
    releaseAmount: fen2yuan(cv.release_amount),
    /** 废除渠道 */
    deleteChannel: cv.delete_channel,
    /** 累计已使用金额 */
    totalUsedBal: fen2yuan(cv.total_used_bal),
    cvFlag: cv.cv_flag,
    cvState: +cv.cv_state,
  };
}

function formatCvData2022(data): GovCVDataRes2022 {
  const voucherItemList: VoucherItem[] = [];
  const expiredVoucherItemList: VoucherItem[] = [];
  const allList = (data.cv_list || []).map(item => fixVoucherItem(item));
  let has2022 = false; // 是否有2022券
  let revoke = false; // 是否有某张券已被撤销

  const govData = {
    govCouponStage: +data.gov_coupon_stage,
    state: +data.reg_state,	// 	登记状态：0-未登记 1-已登记 2-审核通过 3-已撤销（派券前）4-已转移（派券前）5-派券失败 6-派券成功
    enhancedUid: data.enhanced_uid,
    regTime: formatTime(data.reg_time), // Date 登记时间
    confirmTime: formatTime(data.confirm_time), // 审核通过时间
    dispatchTime: formatTime(data.dispatch_time), // 派券成功时间
    systemTime: formatTime(data.system_time), // 系统时间
    dispatchFailReason: data.dispatch_fail_reason || '', // 派券失败的原因
    transationId: data.transation_id || '', // 用户在政府系统的transation_id
    userType: data.is_new_user || '0',
    needShowSuccDialog: false,
  };

  allList.forEach((item) => {
    if (item.cvFlag === CouponType.GOV_COUPON) {
      if (GOV_LIST_2022.includes(item.cvId)) {
        has2022 = true;
      }
      if (item.cvState === RegistState.sendUndo) {
        revoke = true;
      }
      item.sortNum = GOV_LIST_2022.includes(item.cvId) ? 10 : 8;
    } else if (item.cvFlag === CouponType.REFUND_COUPON) {
      item.sortNum = 9;
    } else {
      item.sortNum = 7;
    }
    if (+item.cvState === RegistState.overdue) {
      expiredVoucherItemList.push(item);
    } else if (+item.cvState !== RegistState.preSend) {
      voucherItemList.push(item);
    }
  });

  voucherItemList.sort((item1, item2) => (item2.sortNum || 0) - (item1.sortNum || 0));
  expiredVoucherItemList.sort((item1, item2) => (item2.sortNum || 0) - (item1.sortNum || 0));

  // 特殊情况处理，只有预派发券
  if (has2022 && govData.state === RegistState.sendSucc
    && voucherItemList.length === 0 && expiredVoucherItemList.length === 0) {
    govData.state = RegistState.approved;
  }

  // 派券成功展示时间点
  if (Date.now() >= new Date('2022/04/07 00:00:00').getTime()) {
    govData.needShowSuccDialog = true;
  }

  // 特殊处理部分撤销场景
  if (govData.state === RegistState.sendUndo && !revoke) {
    govData.state = RegistState.sendSucc;
  }

  return {
    govData,
    allList,
    voucherItemList,
    expiredVoucherItemList,
  };
}

/**
 * 查询政府消费券 - 2022
*/
export async function getCvData2022() {
  if (govCVDataRes) {
    return govCVDataRes;
  }

  govCVDataRes = getCvData2022Call();
  return govCVDataRes;
}

export async function getCvData2022Call(): Promise<GovCVDataRes2022> {
  const url = `/app/promotion/gov_coupons/gov_coupon_combined_qry.cgi?t=${new Date().getTime()}`;
  const { data } = await getRequest().get(url);
  return formatCvData2022(data);
}

/**
 * 政府消费券信息查询
 */
export async function getGovCvData(): Promise<GovCVData> {
  const url = `/app/promotion/gov_coupons/gov_coupon_registration_qry.cgi?t=${new Date().getTime()}`;
  const result = await getRequest().get(url);
  const state = +result.reg_state;
  return {
    govCouponStage: +result.gov_coupon_stage,
    state,	// 	登记状态：0-未登记 1-已登记 2-审核通过 3-已撤销（派券前）4-已转移（派券前）5-派券失败 6-派券成功
    enhancedUid: result.enhanced_uid,
    regTime: formatTime(result.reg_time), // Date 登记时间
    confirmTime: formatTime(result.confirm_time), // 审核通过时间
    dispatchTime: formatTime(result.dispatch_time), // 派券成功时间
    systemTime: formatTime(result.system_time), // 系统时间
    dispatchFailReason: result.dispatch_fail_reason || '', // 派券失败的原因
    transationId: result.transation_id || '', // 用户在政府系统的transation_id
    couponBalance: fen2yuan(result.coupon_balance), // 消费券余额
    couponExpireTime: formatTime(result.coupon_expire_time), // 消费券失效时间 '2021-06-15 23:59:59'
    userCvId: result.user_cv_id, // 用户消费券ID
    releaseAmount: fen2yuan(result.release_amount),  // 资金撤回金额 大于0表示有做过撤销
  };
}
/**
 * 加码消费券信息查询
 */
export async function qryPromCvData(): Promise<IPromCVData> {
  const url = `/app/promotion/gov_coupons/prom_registration_qry.cgi?t=${new Date().getTime()}`;
  const result = await getRequest().get(url);

  return {
    state: +result.reg_state,
    govCouponStage: +result.gov_coupon_stage,
    couponBalance: parseInt(result.coupon_balance || '0', 10),
    couponExpireTime: formatTime(result.coupon_expire_time),
    userCvId: result.user_cv_id || '',
    userType: result.is_new_user || '0',
    spChannel: result.sp_channel,
  };
}

/**
 * 加码消费券流水查询
 */
export async function qryPromReward(offset: number, limit: number): Promise<IRewardList> {
  const url = `/app/promotion/gov_coupons/gov_coupon_query_rewardlist.cgi?type=1&offset=${offset}&limit=${limit}&t=${new Date().getTime()}`;
  const result = await getRequest().get(url);

  return {
    num: parseInt(result.num || '0', 10),
    rewardList: fixRewardList(result.award_list || []),
  };
}
/**
 * 查询退款专用券信息
*/
export async function qryCvData(): Promise<RefundCvType[]> {
  const url = '/app/promotion/gov_coupons/prom_qry_user_cv_list.cgi?type=4&offset=0&limit=10';
  const result = await getRequest().get(url);
  result.cv_list = result.cv_list || [];
  return result.cv_list.map(item => fixCvItem(item));
}
function fixCvItem(item: any): RefundCvType {
  return {
    cvStatus: item.cv_status,
    transState: item.trans_state,
    ownState: item.own_state,
    createTime: item.create_time,
    validTime: item.valid_time,
    expireTime: item.expire_time,
    userCvId: item.user_cv_id,
    cvId: item.cv_id,
    cvType: item.cv_type,
    cvName: item.cv_name,
    balance: fen2yuan(item.balance),
  };
}
function fixRewardList(arr: any[]): IRewardItem[] {
  return arr.map((item) => {
    const tmp = {
      awardId: item.award_id,
      type: +item.type, // 1 - 注册奖励 2 - 邀请奖励 3 - 扭蛋奖励 4 - 转移入金
      helperNickName: item.helper_nick_name, //  昵称
      amt: parseInt(item.amt || '0', 10),  // 金额
      state: item.state,  // 状态 1 – 正常 2 – 作废
      createTime: item.create_time, // 创建时间
      modifyTime: item.modify_time, // 修改时间
    };
    return tmp;
  });
}

/**
 * JsApi初始化参数
 */
/**
 * @description 获取jsapi参数
 * @param url [string] 当前页面url
 * @returns
 * @property retcode
 * @property retmsg
 * @property appId [string]
 * @property timeStamp [string]
 * @property nonceStr [string]
 * @property signature [string]
 */
export async function qryWxParam(url): Promise<{
  appId: string;
  timestamp: string;  // 注意大小写
  nonceStr: string;
  signature: string;
}> {
  const result = await getRequest().get(`/app/promotion/jsapi_signature.cgi?url=${encodeURIComponent(url)}`);

  const { appId, timeStamp, nonceStr, signature } = result;
  return {
    appId,
    timestamp: timeStamp,
    nonceStr,
    signature,
  };
}
